import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Tag, Switch, Button, Drawer, Form, Select, TimePicker, InputNumber,
  Radio, Checkbox, Space, Tooltip, Popconfirm, message, Alert, Typography, Divider,
} from 'antd';
import {
  ClockCircleOutlined, PlayCircleOutlined, EditOutlined, HistoryOutlined, ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listJobs, patchJob, pauseJob, resumeJob, runNow, listRuns,
  type ScheduledJob, type JobRun,
} from '../../services/schedulerService';
import {
  jobToForm, formToSchedule, describeJob, humanInterval, DOW_OPTIONS,
  type ScheduleForm, type Frequency,
} from '../../utils/cron';

const { Text } = Typography;

const TIMEZONES = [
  'Africa/Dar_es_Salaam', 'Africa/Nairobi', 'Africa/Kampala', 'Africa/Kigali',
  'Africa/Lagos', 'Africa/Johannesburg', 'UTC',
];

const fmt = (iso?: string | null) => (iso ? dayjs(iso).format('MMM D, YYYY HH:mm') : '—');

const runStatusTag = (s: JobRun['status']) => {
  const color = s === 'published' ? 'green' : s === 'failed' ? 'red' : s === 'skipped' ? 'gold' : 'blue';
  return <Tag color={color}>{s}</Tag>;
};

/** Build a throwaway job from the in-progress form so we can preview its sentence. */
function previewSentence(form: ScheduleForm, tz: string): string {
  const sched = formToSchedule(form);
  if (sched.schedule_type === 'cron' && !sched.cron_expression) return 'Enter a cron expression…';
  const fake: ScheduledJob = {
    id: '', tenant_id: null, job_key: '', subject: '', timezone: tz,
    schedule_type: sched.schedule_type, cron_expression: sched.cron_expression,
    interval_seconds: sched.interval_seconds, enabled: true, max_retries: 0,
    retry_delay_seconds: 0, created_at: '', updated_at: '',
  };
  return describeJob(fake);
}

export const ScheduledJobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Edit drawer state
  const [editing, setEditing] = useState<ScheduledJob | null>(null);
  const [form, setForm] = useState<ScheduleForm | null>(null);
  const [tz, setTz] = useState('Africa/Dar_es_Salaam');
  const [maxRetries, setMaxRetries] = useState(3);
  const [retryDelay, setRetryDelay] = useState(60);
  const [saving, setSaving] = useState(false);

  // Runs drawer state
  const [runsFor, setRunsFor] = useState<ScheduledJob | null>(null);
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listJobs();
      setJobs([...data].sort((a, b) => a.job_key.localeCompare(b.job_key)));
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load scheduled jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggleEnabled = async (job: ScheduledJob, next: boolean) => {
    setBusyId(job.id);
    try {
      const updated = next ? await resumeJob(job.id) : await pauseJob(job.id);
      setJobs((prev) => prev.map((j) => (j.id === job.id ? updated : j)));
      message.success(next ? 'Job resumed' : 'Job paused');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not update job');
    } finally {
      setBusyId(null);
    }
  };

  const triggerNow = async (job: ScheduledJob) => {
    setBusyId(job.id);
    try {
      const run = await runNow(job.id);
      if (run.status === 'published') message.success(`Triggered — event published`);
      else message.warning(`Triggered — run ${run.status}${run.error_message ? `: ${run.error_message}` : ''}`);
      void load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.response?.data?.message || 'Trigger failed');
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (job: ScheduledJob) => {
    setEditing(job);
    setForm(jobToForm(job));
    setTz(job.timezone || 'Africa/Dar_es_Salaam');
    setMaxRetries(job.max_retries ?? 3);
    setRetryDelay(job.retry_delay_seconds ?? 60);
  };

  const saveEdit = async () => {
    if (!editing || !form) return;
    const sched = formToSchedule(form);
    if (sched.schedule_type === 'cron' && !sched.cron_expression) {
      message.error('Schedule is incomplete');
      return;
    }
    setSaving(true);
    try {
      const updated = await patchJob(editing.id, {
        ...sched,
        timezone: tz,
        max_retries: maxRetries,
        retry_delay_seconds: retryDelay,
      });
      setJobs((prev) => prev.map((j) => (j.id === editing.id ? updated : j)));
      message.success('Schedule updated');
      setEditing(null);
      setForm(null);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not save schedule');
    } finally {
      setSaving(false);
    }
  };

  const openRuns = async (job: ScheduledJob) => {
    setRunsFor(job);
    setRunsLoading(true);
    try {
      setRuns(await listRuns(job.id));
    } catch {
      message.error('Could not load run history');
    } finally {
      setRunsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Job',
        key: 'job',
        render: (_: unknown, j: ScheduledJob) => (
          <div>
            <div style={{ fontWeight: 600 }}>{j.job_key}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{j.subject}</Text>
          </div>
        ),
      },
      {
        title: 'Scope',
        key: 'scope',
        render: (_: unknown, j: ScheduledJob) =>
          j.tenant_id ? <Tag>tenant</Tag> : <Tag color="purple">global</Tag>,
      },
      {
        title: 'Schedule',
        key: 'schedule',
        render: (_: unknown, j: ScheduledJob) => (
          <div>
            <div>{describeJob(j)}</div>
            <Text code style={{ fontSize: 11 }}>
              {j.schedule_type === 'interval' ? `every ${humanInterval(j.interval_seconds || 0)}` : j.cron_expression}
            </Text>
          </div>
        ),
      },
      { title: 'Next run', key: 'next', render: (_: unknown, j: ScheduledJob) => fmt(j.next_run_at) },
      { title: 'Last run', key: 'last', render: (_: unknown, j: ScheduledJob) => fmt(j.last_run_at) },
      {
        title: 'Enabled',
        key: 'enabled',
        render: (_: unknown, j: ScheduledJob) => (
          <Switch
            checked={j.enabled}
            loading={busyId === j.id}
            onChange={(v) => toggleEnabled(j, v)}
          />
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: unknown, j: ScheduledJob) => (
          <Space>
            <Tooltip title="Edit schedule">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(j)} />
            </Tooltip>
            <Popconfirm
              title="Run this job now?"
              description="Fires the trigger event immediately."
              onConfirm={() => triggerNow(j)}
              okText="Run now"
            >
              <Tooltip title="Run now">
                <Button size="small" icon={<PlayCircleOutlined />} loading={busyId === j.id} />
              </Tooltip>
            </Popconfirm>
            <Tooltip title="Run history">
              <Button size="small" icon={<HistoryOutlined />} onClick={() => openRuns(j)} />
            </Tooltip>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyId],
  );

  const setF = (patch: Partial<ScheduleForm>) => setForm((f) => (f ? { ...f, ...patch } : f));

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>
            <ClockCircleOutlined /> Scheduled Jobs
          </h2>
          <Text type="secondary">
            Platform automation schedules. Each job fires a trigger event on its schedule; the owning service does the work.
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => load()}>Refresh</Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={jobs}
        columns={columns as any}
        pagination={false}
        size="middle"
      />

      {/* ---- Edit schedule drawer ---- */}
      <Drawer
        title={editing ? `Edit schedule — ${editing.job_key}` : ''}
        width={460}
        open={!!editing}
        onClose={() => { setEditing(null); setForm(null); }}
        extra={
          <Space>
            <Button onClick={() => { setEditing(null); setForm(null); }}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={saveEdit}>Save</Button>
          </Space>
        }
      >
        {form && (
          <Form layout="vertical">
            <Form.Item label="Frequency">
              <Radio.Group
                value={form.frequency}
                onChange={(e) => setF({ frequency: e.target.value as Frequency })}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="daily">Daily</Radio.Button>
                <Radio.Button value="weekly">Weekly</Radio.Button>
                <Radio.Button value="monthly">Monthly</Radio.Button>
                <Radio.Button value="interval">Interval</Radio.Button>
                <Radio.Button value="advanced">Advanced</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {(form.frequency === 'daily' || form.frequency === 'weekly' || form.frequency === 'monthly') && (
              <Form.Item label="Time of day">
                <TimePicker
                  value={dayjs(form.time, 'HH:mm')}
                  format="HH:mm"
                  minuteStep={5}
                  allowClear={false}
                  onChange={(v) => setF({ time: v ? v.format('HH:mm') : '00:00' })}
                />
              </Form.Item>
            )}

            {form.frequency === 'weekly' && (
              <Form.Item label="Days of week">
                <Checkbox.Group
                  options={DOW_OPTIONS}
                  value={form.daysOfWeek}
                  onChange={(v) => setF({ daysOfWeek: v as number[] })}
                />
              </Form.Item>
            )}

            {form.frequency === 'monthly' && (
              <Form.Item label="Day of month" extra="1–28 (avoids short months)">
                <InputNumber
                  min={1}
                  max={28}
                  value={form.dayOfMonth}
                  onChange={(v) => setF({ dayOfMonth: Number(v) || 1 })}
                />
              </Form.Item>
            )}

            {form.frequency === 'interval' && (
              <Form.Item label="Run every">
                <Space>
                  <InputNumber
                    min={1}
                    value={form.intervalValue}
                    onChange={(v) => setF({ intervalValue: Number(v) || 1 })}
                  />
                  <Select
                    style={{ width: 120 }}
                    value={form.intervalUnit}
                    onChange={(v) => setF({ intervalUnit: v })}
                    options={[{ label: 'minutes', value: 'minutes' }, { label: 'hours', value: 'hours' }]}
                  />
                </Space>
              </Form.Item>
            )}

            {form.frequency === 'advanced' && (
              <Form.Item label="Cron expression" extra="Standard 5-field cron (min hour dom mon dow)">
                <input
                  className="ant-input"
                  style={{ fontFamily: 'monospace' }}
                  value={form.cron}
                  onChange={(e) => setF({ cron: e.target.value })}
                  placeholder="0 6 25 * *"
                />
              </Form.Item>
            )}

            <Form.Item label="Timezone">
              <Select
                value={tz}
                onChange={setTz}
                options={TIMEZONES.map((t) => ({ label: t, value: t }))}
                disabled={form.frequency === 'interval'}
              />
            </Form.Item>

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Schedule preview"
              description={previewSentence(form, tz)}
            />

            <Divider style={{ margin: '8px 0' }}>Publish retries</Divider>
            <Space size="large">
              <Form.Item label="Max retries" style={{ marginBottom: 0 }}>
                <InputNumber min={0} max={10} value={maxRetries} onChange={(v) => setMaxRetries(Number(v) || 0)} />
              </Form.Item>
              <Form.Item label="Retry delay (s)" style={{ marginBottom: 0 }}>
                <InputNumber min={5} max={3600} value={retryDelay} onChange={(v) => setRetryDelay(Number(v) || 60)} />
              </Form.Item>
            </Space>
          </Form>
        )}
      </Drawer>

      {/* ---- Run history drawer ---- */}
      <Drawer
        title={runsFor ? `Run history — ${runsFor.job_key}` : ''}
        width={620}
        open={!!runsFor}
        onClose={() => setRunsFor(null)}
      >
        <Table
          rowKey="id"
          loading={runsLoading}
          dataSource={runs}
          pagination={{ pageSize: 10 }}
          size="small"
          columns={[
            { title: 'Started', key: 's', render: (_: unknown, r: JobRun) => fmt(r.started_at) },
            { title: 'Status', key: 'st', render: (_: unknown, r: JobRun) => runStatusTag(r.status) },
            { title: 'Attempt', dataIndex: 'attempt', key: 'a' },
            { title: 'Reason', dataIndex: 'reason', key: 'r' },
            {
              title: 'Error',
              key: 'e',
              render: (_: unknown, r: JobRun) =>
                r.error_message ? <Text type="danger" style={{ fontSize: 12 }}>{r.error_message}</Text> : '—',
            },
          ] as any}
        />
      </Drawer>
    </div>
  );
};

export default ScheduledJobsPage;
